import React, { useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  Box,
  IconButton,
  Chip,
  Divider,
  alpha,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "@mui/material/styles";
import { tokens } from "../../theme";

const ShowMoreDialog = ({
  open,
  onClose,
  data,
  title = "Details",
  maxWidth = "xl",
  ignoreFields = [],
  forceShowFields = [],
  customRenderers = {},
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";

  // Theme-aware color palette
  const palette = {
    dialogBg: isDark ? colors.primary[500] : "#f0f4f8",
    titleBg: isDark ? colors.primary[400] : "#1F3A5F",
    titleColor: isDark ? colors.grey[100] : "#ffffff",
    contentBg: isDark ? colors.primary[500] : "#e4ebf3",
    cardBg: isDark ? colors.primary[400] : "#dbe3ec",
    cardHover: isDark ? colors.primary[300] : "#c8d5e3",
    labelBg: isDark ? colors.blueAccent[700] : "#426185",
    labelColor: "#ffffff",
    valueColor: colors.greenAccent[400],          // ✅ matches COLUMNS/DENSITY/EXPORT
    footerBg: isDark ? colors.primary[400] : "#5b677d",
    footerColor: "#ffffff",
    closeBtn: isDark ? colors.blueAccent[400] : "#1565c0",
    closeBtnHov: isDark ? colors.blueAccent[700] : "#0d47a1",
  };

  // Default fields to ignore (company-related fields and common unwanted fields)
  const defaultIgnoreFields = [
    'companyName', 'companyId', 'company',
    'createdBy', 'updatedBy',
    'regionId', 'region', 'isActive', 'uomId', 'formattedSaleDate',
     'formattedCreatedDate', 'formattedUpdatedDate', 'firstRecordInPage','lastRecordInPage',
     'totalPages','totalRecords', 'pageSize', 'currentPage','int', 'formattedDate','formattedOrderDate'
  ];

  // Combine default ignore fields with user provided ignoreFields,
  // then remove any that are forced to show
  const allIgnoreFields = [...new Set([...defaultIgnoreFields, ...ignoreFields])]
    .filter(field => !forceShowFields.includes(field));

  const dataEntries = useMemo(() => {
    if (!data) return [];
    return Object.entries(data).filter(([key]) => !allIgnoreFields.includes(key));
  }, [data, allIgnoreFields]);

  // Parse common backend timestamp shapes into Date reliably.
  const parseDateValue = (value) => {
    if (value instanceof Date) return value;
    if (typeof value === "number") return new Date(value);
    if (typeof value !== "string") return null;

    const input = value.trim();
    if (!input) return null;

    // YYYY-MM-DD (date only)
    if (/^\d{4}-\d{2}-\d{2}$/.test(input)) {
      return new Date(`${input}T00:00:00+03:00`);
    }

    // YYYY-MM-DDTHH:mm:ss(.fff) or YYYY-MM-DD HH:mm:ss(.fff) with no timezone
    if (/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2}(\.\d+)?$/.test(input)) {
      const normalized = input.replace(" ", "T");
      return new Date(`${normalized}+03:00`);
    }

    // ISO with timezone already present
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+\-]\d{2}:\d{2})$/.test(input)) {
      return new Date(input);
    }

    // Fallback: let JS parse other forms
    return new Date(input);
  };

  // Function to format date strings
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";

    try {
      const date = parseDateValue(dateString);
      if (!date || isNaN(date.getTime())) return String(dateString);

      return date.toLocaleString("en-KE", {
        timeZone: "Africa/Nairobi",
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    } catch (error) {
      return String(dateString);
    }
  };

  // Check if a field is a date field
  const isDateField = (key) => {
    const lowerKey = key.toLowerCase();
    if (lowerKey.endsWith('by') || lowerKey.endsWith('name')) return false;
    const dateIndicators = ['date', 'Date', 'time', 'Time', 'created', 'updated', 'timestamp'];
    return dateIndicators.some(indicator => lowerKey.includes(indicator.toLowerCase()));
  };

  // Check if a field should be ignored (company-related)
  const isCompanyField = (key) => {
    const companyIndicators = ['company', 'Company', 'firm', 'Firm', 'organization', 'Organization'];
    return companyIndicators.some(indicator => key.toLowerCase().includes(indicator.toLowerCase()));
  };

  const parseChangedData = (value) => {
  if (!value) return [];

  try {
    const parsed = typeof value === "string" ? JSON.parse(value) : value;

    if (!Array.isArray(parsed)) return [];

    return parsed;
  } catch (error) {
    console.error("Failed to parse changedData:", error);
    return [];
  }
};

const formatChangedValue = (value, fieldName) => {
  if (value === null || value === undefined || value === "") {
    return "N/A";
  }

  const field = String(fieldName || "").toLowerCase();

  // Date fields
  const dateFields = [
    "date",
    "lastupdated",
    "stockduedate",
    "createddate",
    "updateddate",
    "auditdate",
    "paymentdate",
    "orderdate",
    "approveddate",
    "initiateddate",
    "expirydate",
  ];

  const isDateField =
    dateFields.some(
      (dateField) =>
        field === dateField ||
        field.includes(dateField)
    );

  if (isDateField) {
    return formatDate(value);
  }

  // Numeric fields
  const numericFields = [
    "quantity",
    "averagebp",
    "averagesp",
    "expectedprofit",
    "cartons",
    "totalvalue",
    "totalcostprice",
  ];

  if (numericFields.includes(field)) {
    const number = Number(value);

    if (!Number.isNaN(number)) {
      // Preserve integer fields as integers
      if (
        field === "quantity" ||
        field === "cartons"
      ) {
        return number.toLocaleString("en-KE");
      }

      return number.toLocaleString("en-KE", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }
  }

  return String(value);
};
const renderChangedData = (value) => {
  const changes = parseChangedData(value);

  if (!changes.length) {
    return (
      <Typography
        variant="body2"
        sx={{
          color: palette.valueColor,
          textAlign: "center",
        }}
      >
        N/A
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        overflowX: "auto",
      }}
    >
      <Box
        sx={{
          minWidth: 650,
          borderRadius: 1.5,
          overflow: "hidden",
          border: `1px solid ${alpha(palette.valueColor, 0.25)}`,
        }}
      >
        {/* Header */}
        <Grid
          container
          sx={{
            bgcolor: palette.labelBg,
            color: palette.labelColor,
            fontWeight: 700,
          }}
        >
          <Grid item xs={4} sx={{ p: 1.2 }}>
            <Typography variant="body2" fontWeight={700}>
              Column
            </Typography>
          </Grid>

          <Grid item xs={4} sx={{ p: 1.2 }}>
            <Typography variant="body2" fontWeight={700}>
              Old Value
            </Typography>
          </Grid>

          <Grid item xs={4} sx={{ p: 1.2 }}>
            <Typography variant="body2" fontWeight={700}>
              New Value
            </Typography>
          </Grid>
        </Grid>

        {/* Changes */}
        {changes.map((change, index) => (
          <Grid
            container
            key={`${change.column}-${index}`}
            sx={{
              bgcolor:
                index % 2 === 0
                  ? palette.cardBg
                  : palette.contentBg,
              borderTop: `1px solid ${alpha(
                palette.valueColor,
                0.15
              )}`,
              alignItems: "center",
            }}
          >
            {/* Column */}
            <Grid item xs={4} sx={{ p: 1.2 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  color: palette.titleBg,
                }}
              >
                {formatFieldName(change.column)}
              </Typography>
            </Grid>

            {/* Old */}
            <Grid item xs={4} sx={{ p: 1.2 }}>
              <Typography
                variant="body2"
                sx={{
                  color: palette.valueColor,
                  fontWeight: 600,
                  wordBreak: "break-word",
                }}
              >
                {formatChangedValue(
                  change.old,
                  change.column
                )}
              </Typography>
            </Grid>

            {/* New */}
            <Grid item xs={4} sx={{ p: 1.2 }}>
              <Typography
                variant="body2"
                sx={{
                  color: palette.valueColor,
                  fontWeight: 700,
                  wordBreak: "break-word",
                }}
              >
                {formatChangedValue(
                  change.new,
                  change.column
                )}
              </Typography>
            </Grid>
          </Grid>
        ))}
      </Box>
    </Box>
  );
};
  const renderValue = (key, value) => {
    // Special handling for audit changedData
  if (key.toLowerCase() === "changeddata") {
    return renderChangedData(value);
  }
    // Use custom renderer if provided
    if (customRenderers[key]) return customRenderers[key](value);

    // Handle null/undefined
    if (value === null || value === undefined)
      return <Chip label="N/A" size="small" color="default" />;

    // Handle boolean values
    if (typeof value === "boolean")
      return (
        <Chip
          label={value ? "Yes" : "No"}
          size="small"
          color={value ? "success" : "error"}
        />
      );

    // Handle date fields — use greenAccent color
    if (isDateField(key) && (typeof value === 'string' || typeof value === 'number')) {
      return (
        <Box sx={{ textAlign: "center" }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: palette.valueColor }}
          >
            {formatDate(value)}
          </Typography>
        </Box>
      );
    }

    // Handle arrays
    if (Array.isArray(value))
      return (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, justifyContent: "center" }}>
          {value.map((v, i) => (
            <Chip key={i} label={String(v)} size="small" variant="outlined"
              sx={{ color: palette.valueColor, borderColor: alpha(palette.valueColor, 0.5) }}
            />
          ))}
        </Box>
      );

    // Handle objects
    if (typeof value === "object" && !(value instanceof Date))
      return (
        <Box
          component="pre"
          sx={{
            m: 0,
            p: 1,
            bgcolor: isDark ? colors.primary[500] : colors.primary[900],
            borderRadius: 1,
            fontSize: "0.75rem",
            overflow: "auto",
            maxHeight: "150px",
            textAlign: "left",
            color: palette.valueColor,
          }}
        >
          {JSON.stringify(value, null, 2)}
        </Box>
      );

    // Handle URLs
    if (typeof value === "string" && /^https?:\/\//i.test(value))
      return (
        <a
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: palette.valueColor,
            textDecoration: "underline",
            wordBreak: "break-all",
            textAlign: "center",
            display: "block",
          }}
        >
          {value}
        </a>
      );

    // Handle numbers — price fields in green accent, others also green accent
    if (typeof value === "number") {
      const isPriceField =
        key.toLowerCase().includes('price') ||
        key.toLowerCase().includes('amount') ||
        key.toLowerCase().includes('cost') ||
        key.toLowerCase().includes('value') ||
        key.toLowerCase().includes('balance');

      if (isPriceField) {
        return (
          <Typography
            variant="body2"
            sx={{ fontWeight: 700, color: palette.valueColor, textAlign: "center" }}
          >
            KSh {value.toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Typography>
        );
      }

      return (
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, textAlign: "center", color: palette.valueColor }}
        >
          {value.toLocaleString()}
        </Typography>
      );
    }

    // Default string representation — green accent
    return (
      <Typography
        variant="body2"
        sx={{ textAlign: "center", wordBreak: "break-word", color: palette.valueColor }}
      >
        {String(value)}
      </Typography>
    );
  };

  // Format field names for display
  const formatFieldName = (key) => {
    return key
      .replace(/_/g, " ")
      .replace(/([A-Z])/g, " $1")
      .replace(/([A-Z][a-z])/g, " $1")
      .replace(/\s+/g, " ")
      .trim()
      .split(" ")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  if (!data) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={maxWidth}
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: "90vh",
          bgcolor: palette.dialogBg,
          border: isDark ? `1px solid ${colors.blueAccent[700]}` : "none",
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 700,
          fontSize: "1.2rem",
          color: palette.titleColor,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pr: 1,
          bgcolor: palette.titleBg,
          borderTopLeftRadius: "12px",
          borderTopRightRadius: "12px",
        }}
      >
        {title}
        <IconButton
          onClick={onClose}
          sx={{
            color: "#fff",
            "&:hover": { bgcolor: "rgba(194, 194, 194, 0.15)" },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3, bgcolor: palette.contentBg }}>
        {dataEntries.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              No data to display
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {dataEntries.map(([key, value]) => (
              <Grid item
                  xs={12}
                  sm={key.toLowerCase() === "changeddata" ? 12 : 6}
                  md={key.toLowerCase() === "changeddata" ? 12 : 4}
                  key={key}     
                  >
                <Box
                  sx={{
                    p: 2,
                    bgcolor: palette.cardBg,
                    borderRadius: 2,
                    boxShadow: isDark
                      ? `0px 2px 8px ${alpha(colors.primary[900], 0.6)}`
                      : "0px 2px 5px rgba(0,0,0,0.15)",
                    border: isDark ? `1px solid ${alpha(colors.blueAccent[700], 0.5)}` : "none",
                    transition: "transform 0.2s, box-shadow 0.2s",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    "&:hover": {
                      transform: "translateY(-3px)",
                      boxShadow: isDark
                        ? `0px 4px 16px ${alpha(colors.blueAccent[700], 0.5)}`
                        : "0px 4px 12px rgba(0,0,0,0.25)",
                      bgcolor: palette.cardHover,
                    },
                  }}
                >
                  {/* Field Label Header */}
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 800,
                      bgcolor: palette.labelBg,
                      color: palette.labelColor,
                      borderRadius: 1,
                      p: 1,
                      textAlign: "center",
                      mb: 1.5,
                      fontSize: "0.9rem",
                    }}
                  >
                    {formatFieldName(key)}
                  </Typography>

                  {/* Field Value — always green accent */}
                  <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {renderValue(key, value)}
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        )}
      </DialogContent>

      <Divider sx={{ borderColor: isDark ? colors.blueAccent[700] : undefined }} />

      <DialogActions sx={{ p: 2, bgcolor: palette.footerBg }}>
        <Typography variant="caption" sx={{ color: palette.footerColor, mr: "auto" }}>
          {dataEntries.length} {dataEntries.length === 1 ? "field" : "fields"} displayed
        </Typography>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            bgcolor: palette.closeBtn,
            color: "#fff",
            fontWeight: 600,
            textTransform: "none",
            px: 3,
            "&:hover": { bgcolor: palette.closeBtnHov },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShowMoreDialog;
